import { LegalLayout, Section } from "@/components/LegalLayout";

const EFFECTIVE = "1 January 2026";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Use" effective={EFFECTIVE}>
      <p className="text-[15px] leading-relaxed text-foreground/80 mb-8">
        These Terms of Use (the “Terms”) form a binding agreement between you and BIIPP (“BIIPP”, “we”,
        “us” or “our”) governing your access to and use of the BIIPP website, applications and services
        (together, the “Service”). By creating an account, accessing or using the Service, you agree to
        these Terms. If you do not agree, do not use the Service.
      </p>

      <Section heading="1. The Service">
        <p>
          BIIPP is a tool that helps organizations record items that have an expiry or renewal date and
          receive reminders about them. The Service is provided to assist you; it is not a substitute for
          your own diligence. You remain solely responsible for meeting your legal, regulatory, contractual
          and other obligations, including renewing anything you track in BIIPP on time.
        </p>
      </Section>

      <Section heading="2. Eligibility and accounts">
        <p>
          You must be at least 18 years old and capable of forming a binding contract to use the Service.
          The first user of an organization is its “Owner”. You are responsible for the accuracy of the
          information you provide, for maintaining the confidentiality of your credentials, and for all
          activity that occurs under your account. Notify us promptly of any unauthorised use.
        </p>
      </Section>

      <Section heading="3. Acceptable use">
        <p>You agree not to, and not to permit anyone to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>use the Service in violation of any applicable law or regulation;</li>
          <li>upload unlawful, infringing, or malicious content, or any code intended to disrupt the Service;</li>
          <li>attempt to access data belonging to another organization, or probe, scan or breach security or authentication measures;</li>
          <li>reverse engineer, decompile, resell, or create derivative works from the Service except as permitted by law;</li>
          <li>use the Service to send unsolicited communications or to store especially sensitive categories of data beyond what the Service is designed for.</li>
        </ul>
        <p>We may suspend or terminate access that we reasonably believe violates these Terms or poses a risk to the Service or other users.</p>
      </Section>

      <Section heading="4. Your content">
        <p>
          You retain ownership of the data and content you submit to the Service (“Customer Data”). You
          grant BIIPP a worldwide, non-exclusive licence to host, process, transmit and display Customer
          Data solely to provide and improve the Service, to send the reminders and notifications you
          configure, and as otherwise described in our Privacy Policy. You represent that you have all
          rights necessary to submit Customer Data and that doing so does not violate any law or third-party right.
        </p>
      </Section>

      <Section heading="5. Our intellectual property">
        <p>
          The Service, including its software, design, text, and trademarks, is owned by BIIPP and its
          licensors and is protected by intellectual property laws. Except for the limited right to use the
          Service under these Terms, no rights are granted to you. All rights not expressly granted are reserved.
        </p>
      </Section>

      <Section heading="6. Third-party services">
        <p>
          The Service relies on third-party providers (for example, to deliver email). Your use of the
          Service may be subject to those providers’ terms. We are not responsible for third-party services
          and do not guarantee the delivery, timing or receipt of any email or notification.
        </p>
      </Section>

      <Section heading="7. No warranties">
        <p className="font-medium text-foreground">
          THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE”, WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS, IMPLIED OR STATUTORY.
        </p>
        <p>
          To the fullest extent permitted by law, BIIPP disclaims all warranties, including any implied
          warranties of merchantability, fitness for a particular purpose, title, and non-infringement, and
          any warranties arising from course of dealing or usage of trade. BIIPP does not warrant that the
          Service will be uninterrupted, timely, secure, error-free, or that any reminder or notification
          will be sent, delivered, received, or accurate. You acknowledge that reminders are a convenience
          only and that you must not rely on the Service as your sole means of tracking any deadline. Any
          reliance on the Service is at your own risk.
        </p>
      </Section>

      <Section heading="8. Limitation of liability">
        <p>
          To the fullest extent permitted by law, BIIPP and its officers, employees and suppliers will not
          be liable for any indirect, incidental, special, consequential, exemplary or punitive damages, or
          for any loss of profits, revenue, data, goodwill, or business opportunities, or for any missed
          deadline, lapsed permit, policy, licence or registration, or any resulting fines or penalties,
          however caused and under any theory of liability, even if advised of the possibility of such damages.
        </p>
        <p>
          BIIPP’s total aggregate liability arising out of or relating to the Service or these Terms will
          not exceed the greater of (a) the amount you paid us for the Service in the three (3) months
          immediately preceding the event giving rise to the claim, or (b) USD 50. Some jurisdictions do not
          allow certain limitations, so some of the above may not apply to you; in that case our liability
          is limited to the smallest amount permitted by law.
        </p>
      </Section>

      <Section heading="9. Indemnification">
        <p>
          You agree to indemnify, defend and hold harmless BIIPP from and against any claims, liabilities,
          damages, losses and expenses (including reasonable legal fees) arising out of or connected with
          your Customer Data, your use of the Service, or your violation of these Terms or of any law or
          third-party right.
        </p>
      </Section>

      <Section heading="10. Suspension and termination">
        <p>
          You may stop using the Service at any time, and an Owner may delete their organization and its
          data from within the Service. We may suspend or terminate your access at any time if you breach
          these Terms or to protect the Service. Upon termination, your right to use the Service ceases.
          Sections that by their nature should survive termination will survive, including ownership,
          disclaimers, limitation of liability and indemnification.
        </p>
      </Section>

      <Section heading="11. Changes to the Service and these Terms">
        <p>
          We may modify or discontinue the Service, in whole or in part, at any time. We may also update
          these Terms from time to time; the updated version will be indicated by an updated effective date
          and will be effective once posted. Your continued use of the Service after changes take effect
          constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section heading="12. Governing law">
        <p>
          These Terms are governed by the laws of the jurisdiction in which BIIPP is established, without
          regard to its conflict-of-laws rules. You agree to the exclusive jurisdiction of the courts
          located there for any dispute that is not subject to arbitration or small-claims resolution.
        </p>
      </Section>

      <Section heading="13. Contact">
        <p>Questions about these Terms can be sent to <a className="underline underline-offset-4" href="mailto:legal@biipp.com">legal@biipp.com</a>.</p>
      </Section>
    </LegalLayout>
  );
}
